import React, { useState, useEffect, useRef } from 'react';
import ConditionUpdateModal from '../resourceManagement/components/ConditionUpdateModal';
import LayoutDefault from '../../layouts/LayoutDefault';
import { PrintIcon, DownloadIcon, UploadIcon, ArrowDownIcon, DeliveryDocumentIcon } from '../../utils/icons';
import { Eye, FileText } from 'lucide-react';
import { useSession } from 'next-auth/react';
import { FilePreviewModal } from '@/templates/Admin/components/Manager/FilePreviewModal';
import { useIdPath } from '@/contexts/IdPathProvider';
import { assetLoanService } from '@/services/assetLoan.service';
import { LoanRequest, LoanItem } from '@/types/asset-loan.types';
import { useToast } from '@/templates/Admin/components/Toast';
import axiosClient from '@/templates/Admin/variants/default/utils/api/base';
import apiEndPointList from '@/templates/Admin/variants/default/utils/constant/apiEndPointsList';
import { commentsService } from '../../utils/services/comments';

interface TrackDeliveryStatusDetailProps {
  isOpen: boolean;
  onClose: (shouldRefresh?: boolean) => void;
  deliveryId?: string;
  isSidebarOpen?: boolean;
  isModal?: boolean;
  deliveryStatus?: 'new-incoming' | 'update' | 'returned' | 'not-returned';
}

const TrackDeliveryStatusDetail: React.FC<TrackDeliveryStatusDetailProps> = ({
  isOpen,
  onClose,
  deliveryId,
  isSidebarOpen = true,
  isModal = false,
  deliveryStatus = 'new-incoming',
}) => {
  const { data: session } = useSession();
  const { idPath, namePath } = useIdPath();
  const [loading, setLoading] = useState(false);
  const [loanRequest, setLoanRequest] = useState<LoanRequest | null>(null);
  const [items, setItems] = useState<LoanItem[]>([]);
  const isBorrowing = loanRequest?.status === 'approved' || loanRequest?.status === 'handover_pending';
  const [notes, setNotes] = useState("");
  const [uploadedFile, setUploadedFile] = useState<File | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const toast = useToast();

  const [updatingItemId, setUpdatingItemId] = useState<number | string | null>(null);
  const [itemConditions, setItemConditions] = useState<Record<number | string, { note: string; fileUrl: string | null; returned: boolean }>>({});
  const [itemConditionNote, setItemConditionNote] = useState('');
  const [savingDelivery, setSavingDelivery] = useState(false);

  // States for file preview
  const [previewFileUrl, setPreviewFileUrl] = useState<string | null>(null);
  const [previewFileName, setPreviewFileName] = useState<string | null>(null);
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);

  useEffect(() => {
    if (isOpen && deliveryId && idPath) {
      const fetchData = async () => {
        setLoading(true);
        try {
          const loanIdNum = parseInt(deliveryId);
          if (!isNaN(loanIdNum)) {
            const loanData = await assetLoanService.getLoanRequestById(idPath, loanIdNum);
            setLoanRequest(loanData);
            
            const loanItemsData = await assetLoanService.getLoanItems(idPath, loanIdNum);
            setItems(loanItemsData);
            
            // Pre-populate conditions for already-returned items
            const conditions: Record<number | string, { note: string; fileUrl: string | null; returned: boolean }> = {};
            loanItemsData.forEach((item: any) => {
              if (item.item_status === 'returned') {
                conditions[item.item_id] = { note: item.condition_after_return || 'Đã trả', fileUrl: null, returned: true };
              }
            });
            setItemConditions(conditions);
          }
        } catch (error) {
          console.error("Error fetching delivery details", error);
          toast.showError("Không thể tải thông tin phiếu mượn");
        } finally {
          setLoading(false);
        }
      };
      fetchData();
    }
  }, [isOpen, deliveryId, idPath]);

  const handlePrint = () => {
    window.print();
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      const file = e.target.files[0];
      setUploadedFile(file);
      
      const formData = new FormData();
      formData.append('files', file);
      
      setLoading(true);
      try {
        await axiosClient.post(apiEndPointList.admin.UPLOAD_TO_S3(idPath!), formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        toast.showSuccess("Upload thành công: " + file.name);
      } catch (err) {
        console.error("Upload error", err);
        toast.showError("Upload thất bại");
      } finally {
        setLoading(false);
      }
    }
  };

  // Save condition for a single item in local state
  const handleSaveItemCondition = (itemId: number | string, note?: string, fileUrl?: string | null) => {
    const conditionNote = note || itemConditionNote;
    setItemConditions(prev => ({
      ...prev,
      [itemId]: { note: conditionNote, fileUrl: fileUrl || null, returned: true }
    }));
    toast.showSuccess("Đã cập nhật tình trạng hạng mục!");
    setUpdatingItemId(null);
    setItemConditionNote('');
  };

  // Final: submit entire handover record via markAsReturned or markAsBorrowed API
  const handleUpdateDelivery = async () => {
    if (!loanRequest || !idPath) return;
    
    const loanIdNum = loanRequest.loan_id;
    
    const returnedItems = items
      .filter(item => itemConditions[item.item_id]?.returned || item.item_status === 'returned')
      .map(item => ({
        item_id: item.item_id,
        quantity_returned: item.quantity_borrowed || item.quantity_approved || item.quantity_requested || 1,
        condition_rating: 5,
        damage_description: itemConditions[item.item_id]?.note || '',
      }));
    
    if (!isBorrowing && returnedItems.length === 0) {
      toast.showError("Chưa có hạng mục nào được cập nhật tình trạng trả!");
      return;
    }

    setSavingDelivery(true);
    try {
      if (isBorrowing) {
        await assetLoanService.markAsBorrowed(idPath, loanIdNum, {
          actual_borrow_date: new Date().toISOString().split('T')[0],
        } as any);
      } else {
        await assetLoanService.markAsReturned(idPath, loanIdNum, {
          actual_return_date: new Date().toISOString().split('T')[0],
          items: returnedItems,
          notes: notes,
        } as any);
      }

      // Lưu lịch sử tình trạng vào sg3d_comment cho từng asset đã cập nhật
      try {
        const typeRes = await commentsService.getOrCreateCommentObjectTypeByName('asset', 'Lịch sử tình trạng');
        if (typeRes?.success && typeRes.data) {
          const commentPromises = items
            .filter(item => itemConditions[item.item_id]?.returned || item.item_status === 'returned')
            .map(item => {
              const condition = itemConditions[item.item_id];
              const note = condition?.note || item.condition_after_return || 'Đã trả';
              return commentsService.createComment({
                object_id: item.asset_uuid,
                comment_object_type_id: typeRes.data.id,
                content: note,
                attachment: condition?.fileUrl || null,
                star: 5,
              });
            });
          await Promise.allSettled(commentPromises);
        }
      } catch (commentErr) {
        console.error('Lỗi lưu lịch sử tình trạng:', commentErr);
      }

      // Clear cache by re-fetching loan data
      try {
        await axiosClient.get(apiEndPointList.assetLoan.GET_ALL(idPath));
      } catch {}
      toast.showSuccess(isBorrowing ? "Cập nhật biên bản bàn giao thành công! Hiện vật đã được giao cho khách." : "Cập nhật biên bản bàn giao thành công! Hiện vật đã được trả.");
      onClose(true);
    } catch (err: any) {
      console.error("Error updating delivery:", err);
      toast.showError("Cập nhật thất bại: " + (err?.message || 'Lỗi không xác định'));
    } finally {
      setSavingDelivery(false);
    }
  };

  const displayLoanCode = loanRequest?.loan_code || `LR-${deliveryId}`;
  // Build admin QR URL: /{sector}/{project}/admin?loan_approve={loanId}
  const loanUrl = typeof window !== 'undefined' && namePath
    ? `${window.location.origin}/${namePath.replace(/\/admin.*$/, '')}/admin?loan_approve=${loanRequest?.loan_id || deliveryId}`
    : '';

  const overdueItemsCount = items.filter(item => {
    const due = loanRequest?.expected_return_date ? new Date(loanRequest.expected_return_date) : null;
    if (!due) return false;
    return new Date() > due && item.item_status !== 'returned';
  }).length;

  if (!isOpen) return null;

  const renderContent = () => (
    <div style={{ backgroundColor: 'white', minHeight: '100vh', fontSize: '14px' }}>
      {/* Header */}
      <div style={{
        backgroundColor: '#ECECEC',
        padding: '16px',
        marginBottom: '20px'
      }}>
        <div style={{ color: '#B0B0B0', fontSize: '12px', fontWeight: 700, marginBottom: '4px' }}>
          Dành cho đối tác
        </div>
        <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
          <span style={{ fontSize: '18px', fontWeight: 700, textTransform: 'uppercase', color: '#000' }}>
            Biên bản bàn giao
          </span>
          <span style={{ fontSize: '18px', fontWeight: 700, textTransform: 'uppercase', color: '#000' }}>
            {displayLoanCode}
          </span>
        </div>
      </div>

      <div style={{ display: 'flex', gap: '24px', padding: '0 16px', flexDirection: 'column' }}>
        
        {/* General Info Container */}
        <div style={{ display: 'flex', gap: '32px' }}>
          {/* Bên A */}
          <div style={{ flex: 1 }}>
            <h2 style={{ fontSize: '16px', fontWeight: 600, textTransform: 'uppercase', marginBottom: '12px', color: '#000' }}>
              Thông tin chung BÊN A
            </h2>

            <div style={{ display: 'flex', gap: '16px', marginBottom: '12px' }}>
              <div style={{ flex: 1 }}>
                <label style={{ fontSize: '14px', fontWeight: 500, color: '#000', marginBottom: '6px', display: 'block' }}>
                  Người mượn:
                </label>
                <div style={{
                  backgroundColor: '#F8F8F8',
                  height: '36px',
                  borderRadius: '6px',
                  display: 'flex',
                  alignItems: 'center',
                  padding: '0 12px'
                }}>
                  <span style={{ fontSize: '14px', fontWeight: 400, color: '#00A417' }}>
                    {loanRequest?.borrower_name || '—'}
                  </span>
                </div>
              </div>
              <div style={{ flex: 1 }}>
                <label style={{ fontSize: '14px', fontWeight: 500, color: '#000', marginBottom: '6px', display: 'block' }}>
                  Đơn vị:
                </label>
                <div style={{
                  backgroundColor: '#F8F8F8',
                  height: '36px',
                  borderRadius: '6px',
                  display: 'flex',
                  alignItems: 'center',
                  padding: '0 12px'
                }}>
                  <span style={{ fontSize: '14px', fontWeight: 400, color: '#000' }}>
                    {loanRequest?.borrower_department || '—'}
                  </span>
                </div>
              </div>
            </div>

            <div style={{ marginBottom: '16px' }}>
              <label style={{ fontSize: '14px', fontWeight: 500, color: '#000', marginBottom: '6px', display: 'block' }}>
                Lý do mượn:
              </label>
              <div style={{
                backgroundColor: '#F8F8F8',
                minHeight: '36px',
                borderRadius: '6px',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                padding: '8px 12px'
              }}>
                <span style={{ fontSize: '14px', fontWeight: 400, color: '#000' }}>
                  {loanRequest?.loan_purpose || '—'}
                </span>

                {loanRequest?.loan_purpose_file && (
                    <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginTop: '8px' }}>
                        {(() => {
                            let files: any[] = [];
                            try {
                                files = JSON.parse(loanRequest.loan_purpose_file);
                                if (!Array.isArray(files)) {
                                    files = [{ url: loanRequest.loan_purpose_file, name: `File đính kèm 1` }];
                                }
                            } catch (e) {
                                files = [{ url: loanRequest.loan_purpose_file, name: `File đính kèm 1` }];
                            }

                            return files.map((fileObj, index) => {
                                const fileUrl = typeof fileObj === 'string' ? fileObj : fileObj.url;
                                const fileName = typeof fileObj === 'string' ? `File đính kèm ${index + 1}` : (fileObj.name || `File đính kèm ${index + 1}`);
                                const isImage = /\.(jpeg|jpg|png|gif|webp)$/i.test(fileUrl);
                                
                                return (
                                    <div key={index} style={{
                                        display: 'flex', alignItems: 'center', gap: '8px', padding: '6px 12px', 
                                        backgroundColor: '#fff', border: '1px solid #e5e7eb', borderRadius: '8px'
                                    }}>
                                        {isImage ? (
                                            <img src={fileUrl} alt={fileName} style={{ width: '24px', height: '24px', objectFit: 'cover', borderRadius: '4px' }} />
                                        ) : (
                                            <FileText size={20} color="#6b7280" />
                                        )}
                                        <span style={{ fontSize: '12px', color: '#374151', maxWidth: '150px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={fileName}>
                                            {fileName}
                                        </span>
                                        <button 
                                            onClick={() => {
                                                setPreviewFileUrl(fileUrl);
                                                setPreviewFileName(fileName);
                                                setIsPreviewOpen(true);
                                            }}
                                            style={{ 
                                                background: 'none', border: 'none', cursor: 'pointer', padding: '4px',
                                                display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#3b82f6', marginLeft: 'auto'
                                            }}
                                            title="Xem file"
                                        >
                                            <Eye size={16} />
                                        </button>
                                    </div>
                                );
                            });
                        })()}
                    </div>
                )}
              </div>
            </div>

            <div style={{ marginBottom: '16px' }}>
              <label style={{ fontSize: '14px', fontWeight: 500, color: '#000', marginBottom: '6px', display: 'block' }}>
                Liên kết với phiếu mượn:
              </label>
              <div style={{
                backgroundColor: '#F8F8F8',
                height: '36px',
                borderRadius: '6px',
                display: 'flex',
                alignItems: 'center',
                padding: '0 12px'
              }}>
                <span style={{ fontSize: '14px', fontWeight: 400, color: '#000' }}>
                  {displayLoanCode}
                </span>
              </div>
            </div>
          </div>

          {/* Bên B */}
          <div style={{ flex: 1 }}>
            <h2 style={{ fontSize: '16px', fontWeight: 600, textTransform: 'uppercase', marginBottom: '12px', color: '#000' }}>
              Thông tin chung BÊN B
            </h2>

            <div style={{ display: 'flex', gap: '16px', marginBottom: '12px' }}>
              <div style={{ flex: 1 }}>
                <label style={{ fontSize: '14px', fontWeight: 500, color: '#000', marginBottom: '6px', display: 'block' }}>
                  Người nhận bàn giao:
                </label>
                <div style={{
                  backgroundColor: '#F8F8F8',
                  height: '36px',
                  borderRadius: '6px',
                  display: 'flex',
                  alignItems: 'center',
                  padding: '0 12px'
                }}>
                  <span style={{ fontSize: '14px', fontWeight: 400, color: '#00A417' }}>
                    {loanRequest?.approved_by_name || session?.user?.name || 'Admin'}
                  </span>
                </div>
              </div>
              <div style={{ flex: 1 }}>
                <label style={{ fontSize: '14px', fontWeight: 500, color: '#000', marginBottom: '6px', display: 'block' }}>
                  Đơn vị:
                </label>
                <div style={{
                  backgroundColor: '#F8F8F8',
                  height: '36px',
                  borderRadius: '6px',
                  display: 'flex',
                  alignItems: 'center',
                  padding: '0 12px'
                }}>
                  <span style={{ fontSize: '14px', fontWeight: 400, color: '#000' }}>
                    Quản lý kho
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* QR Code Container */}
          <div style={{ width: '120px', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '8px' }}>
            <div style={{
              width: '120px',
              height: '120px',
              borderRadius: '8px',
              backgroundColor: '#f0f0f0',
              boxShadow: '0px 2px 8px rgba(0, 0, 0, 0.1)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              overflow: 'hidden'
            }}>
              <img src={`https://api.qrserver.com/v1/create-qr-code/?size=140x140&data=${encodeURIComponent(loanUrl)}`} alt="QR Code" style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
            </div>
            <span style={{ fontSize: '14px', fontWeight: 600, color: '#000' }}>
              {displayLoanCode}
            </span>
          </div>
        </div>

        {/* Table Section */}
        <div>
          <h2 style={{ fontSize: '16px', fontWeight: 600, textTransform: 'uppercase', marginBottom: '8px', color: '#000' }}>
            hạng mục
          </h2>

          <div style={{ display: 'flex', gap: '24px', marginBottom: '12px' }}>
            <span style={{ fontSize: '14px', fontWeight: 600, color: '#000' }}>
              Tổng số hạng mục: {items.length}
            </span>
            <span style={{ fontSize: '14px', fontWeight: 600, color: '#000' }}>
              Số hạng mục đến hạn trả: {overdueItemsCount}
            </span>
          </div>

          <div style={{ overflowX: 'auto', marginBottom: '24px' }}>
            <table style={{ width: '100%', minWidth: '800px', borderCollapse: 'collapse', textAlign: 'left', fontSize: '14px' }}>
              <thead>
                <tr style={{ borderBottom: '1px solid #ddd' }}>
                  <th style={{ padding: '8px', width: '40px', textAlign: 'center' }}>STT</th>
                  <th style={{ padding: '8px', width: '100px' }}>Hình ảnh</th>
                  <th style={{ padding: '8px' }}>Thông tin</th>
                  <th style={{ padding: '8px', width: '80px', textAlign: 'center' }}>Số lượng</th>
                  <th style={{ padding: '8px', width: '120px', textAlign: 'center' }}>Ngày đến hạn</th>
                  <th style={{ padding: '8px', width: '120px', textAlign: 'center' }}>Ngày trả</th>
                  <th style={{ padding: '8px', width: '180px', textAlign: 'center' }}>Tình trạng khi trả</th>
                </tr>
              </thead>
              <tbody>
                {items.length === 0 && (
                  <tr>
                    <td colSpan={7} style={{ padding: '16px', textAlign: 'center', color: '#666' }}>Không có hạng mục nào</td>
                  </tr>
                )}
                {items.map((item, index) => {
                  // Type casting fallback to any to avoid strict typescript errors if properties are missing
                  const itemData = item as any;
                  const thumb = itemData.asset?.thumbnail || itemData.asset_thumbnail || itemData.thumbnail_url || 'https://placehold.co/100x100?text=No+Image';
                  const typeName = itemData.asset?.type || itemData.asset_type || '—';
                  const titleName = itemData.asset?.title || itemData.asset_name || itemData.title || '—';
                  
                  return (
                    <tr key={item.item_id || index} style={{ borderBottom: '1px solid #eee' }}>
                      <td style={{ padding: '8px', textAlign: 'center' }}>{index + 1}.</td>
                      <td style={{ padding: '8px' }}>
                        <div style={{ width: '64px', height: '64px', borderRadius: '4px', overflow: 'hidden', border: '1px solid #ddd' }}>
                          <img src={thumb} alt="thumbnail" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                        </div>
                      </td>
                      <td style={{ padding: '8px' }}>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                          <span style={{ fontSize: '12px', fontWeight: 600, color: '#666', textTransform: 'uppercase' }}>{typeName}</span>
                          <span style={{ fontSize: '14px', fontWeight: 500, color: '#000' }}>{titleName}</span>
                        </div>
                      </td>
                      <td style={{ padding: '8px', textAlign: 'center' }}>
                        <div style={{ display: 'inline-block', backgroundColor: '#F8F8F8', borderRadius: '4px', padding: '4px 12px' }}>
                          {String(item.quantity_approved || itemData.quantity || 0).padStart(2, '0')}
                        </div>
                      </td>
                      <td style={{ padding: '8px', textAlign: 'center' }}>
                        {loanRequest?.expected_return_date ? new Date(loanRequest.expected_return_date).toLocaleDateString('vi-VN') : '—'}
                      </td>
                      <td style={{ padding: '8px', textAlign: 'center' }}>
                        {loanRequest?.actual_return_date 
                          ? new Date(loanRequest.actual_return_date).toLocaleDateString('vi-VN') 
                          : (!isBorrowing ? new Date().toLocaleDateString('vi-VN') : '--')
                        }
                      </td>
                      <td style={{ padding: '8px', textAlign: 'center' }}>
                        {(item.item_status === 'returned' || itemConditions[item.item_id]?.returned) ? (
                          <div style={{
                            backgroundColor: '#D1FAE5', color: '#065F46',
                            borderRadius: '6px', padding: '8px',
                            fontSize: '13px', fontWeight: 600, textAlign: 'center'
                          }}>
                            ✅ {itemConditions[item.item_id]?.note || 'Đã trả'}
                          </div>
                        ) : (
                          <div 
                            onClick={() => { setUpdatingItemId(item.item_id || index); setItemConditionNote(''); }}
                            style={{
                              backgroundColor: '#ECECEC', color: '#000',
                              borderRadius: '6px', padding: '8px',
                              display: 'flex', justifyContent: 'center', alignItems: 'center',
                              cursor: 'pointer', fontSize: '13px'
                            }}>
                            Cập nhật tình trạng
                          </div>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>

        {/* Actions Section */}
        <div style={{ display: 'flex', gap: '24px', flexWrap: 'wrap' }}>
          
          <div style={{ flex: 1, minWidth: '300px' }}>
            <h3 style={{ fontSize: '16px', fontWeight: 600, textTransform: 'uppercase', marginBottom: '8px', color: '#000' }}>
              Ghi chú của người kiểm tra
            </h3>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="(Không bắt buộc)"
              style={{
                width: '100%',
                minHeight: '80px',
                backgroundColor: '#F8F8F8',
                border: '1px solid #ECECEC',
                borderRadius: '8px',
                padding: '12px',
                fontSize: '14px',
                outline: 'none',
                resize: 'vertical'
              }}
            />
          </div>

          <div style={{ width: '200px' }}>
            <h3 style={{ fontSize: '16px', fontWeight: 600, textTransform: 'uppercase', marginBottom: '8px', color: '#000' }}>
              In biên bản
            </h3>
            <div onClick={handlePrint} style={{
              backgroundColor: '#F8F8F8',
              borderRadius: '8px',
              padding: '8px',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              gap: '8px',
              cursor: 'pointer',
              border: '1px solid #ECECEC'
            }}>
              <PrintIcon />
              <span style={{ fontSize: '14px', fontWeight: 500, color: '#000' }}>In biên bản</span>
            </div>
          </div>

          <div style={{ flex: 1, minWidth: '300px' }}>
            <h3 style={{ fontSize: '16px', fontWeight: 600, textTransform: 'uppercase', marginBottom: '8px', color: '#000' }}>
              Upload biên bản ký
            </h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              {uploadedFile && (
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '8px', backgroundColor: '#eef8ff', borderRadius: '8px' }}>
                  <DownloadIcon />
                  <span style={{ fontSize: '14px', color: '#000' }}>{uploadedFile.name}</span>
                </div>
              )}
              <div onClick={() => fileInputRef.current?.click()} style={{
                backgroundColor: '#F8F8F8',
                borderRadius: '8px',
                padding: '8px',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                gap: '8px',
                cursor: 'pointer',
                border: '1px solid #ECECEC'
              }}>
                <UploadIcon />
                <span style={{ fontSize: '14px', fontWeight: 500, color: '#000' }}>Upload file (.pdf, .jpg, .png)</span>
                <input type="file" ref={fileInputRef} onChange={handleFileChange} style={{ display: 'none' }} accept=".pdf,.jpg,.jpeg,.png" />
              </div>
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginTop: '20px', paddingBottom: '40px' }}>
          <button onClick={handleUpdateDelivery} disabled={savingDelivery} style={{
            backgroundColor: savingDelivery ? '#9CA3AF' : '#5FCD6F',
            borderRadius: '8px',
            padding: '12px 24px',
            flex: 1,
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            gap: '8px',
            border: 'none',
            cursor: savingDelivery ? 'not-allowed' : 'pointer',
            opacity: savingDelivery ? 0.7 : 1,
          }}>
            <DeliveryDocumentIcon />
            <span style={{ fontSize: '16px', fontWeight: 700, color: '#FFF' }}>
              {savingDelivery ? 'Đang xử lý...' : 'Cập nhật vào biên bản'}
            </span>
          </button>

          <button onClick={(e) => { e.preventDefault(); onClose(); }} style={{
            backgroundColor: '#ECECEC',
            borderRadius: '8px',
            padding: '12px 24px',
            width: '140px',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            border: 'none',
            cursor: 'pointer'
          }}>
            <span style={{ fontSize: '16px', fontWeight: 500, color: '#656565' }}>Quay lại</span>
          </button>
        </div>
      </div>
      
      {/* Updating Item Modal - Dùng chung ConditionUpdateModal */}
      <ConditionUpdateModal
        isOpen={updatingItemId !== null}
        onClose={() => { setUpdatingItemId(null); setItemConditionNote(''); }}
        objectId={String(updatingItemId || '')}
        title="Cập nhật tình trạng hạng mục"
        placeholder="Nhập ghi chú tình trạng (ví dụ: bị xước nhẹ, còn nguyên vẹn...)"
        onSave={async (note, fileUrl) => {
          if (updatingItemId !== null) {
            handleSaveItemCondition(updatingItemId, note, fileUrl);
            return true;
          }
          return false;
        }}
      />
    </div>
  );

  if (isModal) {
    return (
      <>
        <div
          style={{
            top: 0, left: 0, right: 0, bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.7)',
            zIndex: 9999, display: 'flex',
            alignItems: 'center', justifyContent: 'center',
            position: 'fixed'
          }}
          onClick={onClose}
        >
          <div
            style={{
              width: '90vw', height: '90vh',
              maxWidth: '1100px',
              backgroundColor: 'white',
              borderRadius: '8px',
              overflow: 'auto',
              boxShadow: '0 4px 20px rgba(0, 0, 0, 0.3)'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {renderContent()}
          </div>
        </div>
        <FilePreviewModal isOpen={isPreviewOpen} onClose={() => setIsPreviewOpen(false)} fileUrl={previewFileUrl} fileName={previewFileName} />
      </>
    );
  }

  return (
    <>
      <LayoutDefault onClose={onClose} isSidebarOpen={isSidebarOpen}>
        {renderContent()}
      </LayoutDefault>
      <FilePreviewModal isOpen={isPreviewOpen} onClose={() => setIsPreviewOpen(false)} fileUrl={previewFileUrl} fileName={previewFileName} />
    </>
  );
};

export default TrackDeliveryStatusDetail;
